import { useEffect, useState } from 'react'
import {
	EuiBasicTable,
	EuiFlexGroup,
	EuiFlexItem,
	EuiPage,
	EuiPageBody,
	EuiPageSection,
	EuiPagination,
	EuiPanel,
} from '@equipmentshare/ds2'

import StyledHeader from '../../../components/StyledHeader'
import SearchBar from '../../../components/SearchBar'
import RefreshButton from '../../../components/RefreshButton'
import BasicActions from '../../../components/BasicActions'
import NeedsApprovalPopover from '../../../components/NeedsApprovalPopover'
import PageTitle from 'views/components/PageTitle/PageTitle'
import InventoryLocationsSubHeader from 'views/components/InventoryLocationsSubHeader'

import getColumns from '../../columns'
import useStoreIds from 'hooks/useStoreIds'
import { availableFilters, buildFilterVariables } from '../../helpers/filters'
import useFilters from 'views/hooks/useFilters'
import { Filter } from 'views/components/Filters'
import { useTransactionsQuery } from '../../../../graphql'
import { TABLE_HEIGHT } from 'views/helpers/utils'

import './index.css'
import { heapEvent } from '@equipmentshare/heaper'
import InventoryLocationsFilter from 'views/components/InventoryLocationsFilter'

export type TransactionViewProps = {
  title: string,
	heaperPageTitle: string,
  variables: any,
  storeIdsKey: 'fromIds' | 'toIds',
  isSearchable?: boolean,
	search?: string,
	setSearch?: ( value: string ) => void,
  columns?: any[],
  defaultFilters?: Filter[],
}

export function getSearchValues( search?: string ) {
	if ( search ) {
		const strings = search.split( ' ' )
		const idPrefix = /^TX-/i

		return strings.map( ( s ) => {
			if ( idPrefix.test( s ) ) {
				const splitS = s.split( idPrefix )
				if ( splitS.length === 2 ) {
					return splitS[ 1 ]
				}
			}
			return s
		} ).join( ' ' )
	}

	return search
}

export default function TransactionView( { title, heaperPageTitle, variables, storeIdsKey, isSearchable, search, setSearch, columns, defaultFilters }: TransactionViewProps ): JSX.Element {
	const { inventoryLocationIds } = useStoreIds()
	const defaultColumns = getColumns()
	// Note: Event name is constructed dynamically, so we need 'as any'. All possible event names are properly typed in heaper-augments.d.ts
	const { filterVariables, render: renderFilters } = useFilters( title, defaultFilters ?? availableFilters, buildFilterVariables, ( args: { filter: string, value: boolean } ) => heapEvent( `Inventory - ${heaperPageTitle} - Change - Filters` as any, args ) )
	const paginationSize = 40
	const [ activePage, setActivePage ] = useState( 0 )
	const { loading, data, refetch } = useTransactionsQuery( {
		variables: {
			search: getSearchValues( search ),
			[ storeIdsKey ]: inventoryLocationIds.map( ( id ) => parseInt( id, 10 ) ),
			...variables,
			...filterVariables,
			size: paginationSize,
			offset: activePage * paginationSize,
		},
	} )

	// Set back to first page
	useEffect( () => {
		setActivePage( 0 )
	}, [ variables, inventoryLocationIds, filterVariables ] )// any change EXCEPT for activePage, offset, size

	// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
	const showPagination = ( !loading && !!( ( data?.transactions?.length && data?.transactions?.length >= paginationSize ) || activePage > 0 ) )

	const getNoItemsMessage = () => {
		if ( loading ) {
			return 'Loading data'
		}
		if ( data?.transactions?.length === 0 && activePage > 0 ) {
			return 'No more records'
		}
		if ( !inventoryLocationIds.length ) {
			return 'Select a store'
		}

		return 'No records found'
	}

	const fullHeight = 'eui-fullHeight'

	// Note: Event names are constructed dynamically, so we need 'as any'. All possible event names are properly typed in heaper-augments.d.ts
	const onClickNeedsApproval = () => heapEvent( `Inventory - ${heaperPageTitle} - Click - Needs Approval Button` as any, {} )
	const onSearchChange = ( s: string ) => {
		heapEvent( `Inventory - ${heaperPageTitle} - Change - Search Input` as any, { search_term: s } )
		setSearch?.( s )
	}

	return (
		<EuiPage paddingSize="l">
			<EuiPageBody className={fullHeight}>
				<PageTitle title={title} />
				<StyledHeader>
					<EuiFlexGroup wrap>
						<EuiFlexItem style={{ minWidth: isSearchable ? 677 : 'inherit' }}>
							<div className="content searchFilter">
								{ isSearchable && setSearch && (
									<SearchBar
										search={search}
										setSearch={onSearchChange}
									/>
								) }
								<InventoryLocationsFilter />
								{renderFilters()}
								<RefreshButton refetchFunction={refetch} />
							</div>
						</EuiFlexItem>
						<EuiFlexItem grow={false}>
							<div className="content actions">
								<NeedsApprovalPopover storeIds={inventoryLocationIds.map( ( id ) => parseInt( id, 10 ) )} onClick={onClickNeedsApproval} />
								<BasicActions />
							</div>
						</EuiFlexItem>
					</EuiFlexGroup>
				</StyledHeader>
				<InventoryLocationsSubHeader pageTitle="Inventory" />

				<EuiPageSection className={fullHeight} paddingSize="none">
					<EuiPanel paddingSize="none" className="eui-yScroll" hasShadow={false} style={{ height: TABLE_HEIGHT }}>
						<EuiBasicTable
							loading={loading}
							noItemsMessage={ getNoItemsMessage() }
							columns={columns ?? defaultColumns}
							items={data?.transactions ?? []}
							hasActions
						/>
					</EuiPanel>
					{ showPagination && (
						<EuiFlexGroup justifyContent="flexEnd">
							<EuiFlexItem grow={false}>
								<EuiPagination
									pageCount={0}
									activePage={activePage}
									onPageClick={( ap ) => setActivePage( ap )}
								/>
							</EuiFlexItem>
						</EuiFlexGroup>
					) }
				</EuiPageSection>
			</EuiPageBody>
		</EuiPage>
	)
}
